/**
 * プロジェクトの中のナビ（pages/ProjectShell.tsx のサイドバー）の並びと、グループの見出しの付け方。
 * 純関数だけ（テストは projectNav.test.ts・.tsx は node で読めないので並びの判断はここに置く）。
 *
 * 並び（2026-09-30 console/core-first・通しの確認 C1・MASTER-PLAN §0「芯 = 公開・Functions と DB・ログイン」）:
 *   概要 → Functions → ホスティング → 利用者 → 設定ファイル → ログイン方法 →（追加の機能）マルチプレイ → X の共有 →（管理）API キー → 監査ログ → メンバー → 人に渡す
 * WHY この並びか: 以前は 概要・利用者・マルチプレイ・X の共有・設定ファイル…で、芯の Functions（サーバーと DB）とホスティングが
 *   ナビの後ろ（スマホ幅では横スクロールの先）にあった。§0 で芯と決めた物を上に、既定 OFF の追加の機能（マルチプレイ・共有）をその下に置く。
 * WHY 芯のまとまりに見出しを付けないか: 概要から ログイン方法 までが「どのプロジェクトでも使う物」。ここに「開発」「モニタリング」のような
 *   見出しを付けると、利用者（データを見る物）と Functions（作る物）が見出しと食い違う。見出しは下の 2 つ（追加の機能・管理）だけにした。
 * 隠した画面（launchHidden.ts: ランキング・メール・販売）は項目を消さずに表に残す = 一覧（LAUNCH_HIDDEN_FEATURES）から外せば元の場所に戻る。
 *
 * このファイルは型の import しか持たない（`node --test` で型の除去だけで動かすため。featureToggle.ts と同じ）。
 */
import type { MessageKey } from './i18n';

export interface NavItem {
  /** ルートの最後の部分（/p/:id/:env/<to>）。launchHidden.ts の NAV_FEATURES のキーと同じ */
  to: string;
  label: MessageKey;
  /** 24x24 の線の絵（path の d） */
  icon: string;
  /** グループの見出し。そのグループの最初の項目にだけ書く（隠れていたら visibleNav が次の見える項目へ移す） */
  group?: MessageKey;
}

export const PROJECT_NAV: readonly NavItem[] = [
  // ---- 芯（見出しなし）
  { to: 'overview', label: 'nav.overview', icon: 'M3 12h4l3-8 4 16 3-8h4' },
  { to: 'functions', label: 'nav.functions', icon: 'M8 5l-5 7 5 7M16 5l5 7-5 7M13.5 4l-3 16' },
  { to: 'hosting', label: 'nav.hosting', icon: 'M3 7l9-4 9 4-9 4-9-4zM3 12l9 4 9-4M3 17l9 4 9-4' },
  { to: 'players', label: 'nav.players', icon: 'M8 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2 20c.6-3.4 3-5 6-5s5.4 1.6 6 5M16 4.5a3.3 3.3 0 0 1 0 6.3M18 15c2 .6 3.4 2.2 4 5' },
  { to: 'config', label: 'nav.config', icon: 'M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M14 4v4M8 10v4M16 16v4' },
  { to: 'sign-in', label: 'nav.signIn', icon: 'M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3' },
  // ---- 追加の機能（どれも既定 OFF・プロジェクトごとに選ぶ物）
  // ランキング（最初の公開では隠す）はマルチプレイの前 = 以前と同じく「遊ぶ人に関わる物」の並び
  { to: 'leaderboards', label: 'nav.leaderboards', icon: 'M4 20V11h4v9M10 20V5h4v15M16 20v-6h4v6', group: 'nav.group.extras' },
  { to: 'multiplayer', label: 'nav.multiplayer', icon: 'M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM17 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM9.5 6.5l5 11M20 5l-3 3M4 19l3-3' },
  { to: 'share', label: 'nav.share', icon: 'M4 13v7h16v-7M12 3v12M7 8l5-5 5 5' },
  // メール配信（2026-09-29 creator/mail-close で隠した）は共有の隣 = 同じ「利用者に届ける」道具
  { to: 'mail', label: 'nav.mail', icon: 'M3 6h18v12H3zM3 7l9 6 9-6' },
  // 利用者への販売（2026-09-29 founder「お金は後で」で隠した）
  { to: 'payments', label: 'nav.payments', icon: 'M3 6h18v12H3zM3 10h18M7 15h4' },
  // ---- 管理
  { to: 'keys', label: 'nav.keys', icon: 'M14.5 9.5a4.5 4.5 0 1 0-4 4.47V14h2v2h2v2h3v-3l-3.5-3.5a4.5 4.5 0 0 0 .5-2z', group: 'nav.group.manage' },
  { to: 'audit', label: 'nav.audit', icon: 'M6 3h9l4 4v14H6zM14 3v5h5M9 12h7M9 16h7' },
  // 共同作業者（2026-10-22 core/handoff-1b・pages/MembersPage.tsx）: 持ち主も共同作業者も見る（共同作業者は一覧と「やめる」だけ）。人に渡すの前 = 渡す前に誰が手伝うかを見る
  { to: 'members', label: 'nav.members', icon: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM3 20c.6-3.4 3-5 6-5s5.4 1.6 6 5M19 8v6M16 11h6' },
  // 人に渡す（2026-10-21 core/handoff-1a・pages/TransferPage.tsx）: 持ち主を替える = 管理の中でいちばん重い操作なので最後
  { to: 'transfer', label: 'nav.transfer', icon: 'M4 12h12M12 6l6 6-6 6M20 4v16' }
];

/**
 * 隠した項目を外した並び。グループの最初の項目が隠れていたら、見出しを同じグループの次の見える項目へ移す。
 * WHY: 以前は項目ごとに見出しを持っていたので、見出しを持つ項目（例: ランキング）を隠すと見出しごと消え、
 *   マルチプレイが前のグループの続きに見えた。グループが丸ごと隠れたら見出しも出さない（次のグループの項目には移さない）
 */
export function visibleNav(items: readonly NavItem[], isHidden: (to: string) => boolean): NavItem[] {
  const out: NavItem[] = [];
  let pending: MessageKey | undefined;
  for (const n of items) {
    // 見出しを持つ項目 = 新しいグループの始まり。前のグループで使われなかった見出しはここで捨てる
    if (n.group) pending = n.group;
    if (isHidden(n.to)) continue;
    const { group: _drop, ...rest } = n;
    out.push(pending ? { ...rest, group: pending } : rest);
    pending = undefined;
  }
  return out;
}

/**
 * プロジェクトの中の各画面の上の小さな行（PageHeader の eyebrow）= プロジェクトの名前。名前が無ければ（空白だけも）スラッグ。
 * WHY（2026-09-30 console/english-pass・E3）: 概要は core-first で名前になったが、ほかの画面（ホスティング・Functions・利用者・設定・API キー…）は
 *   `project.slug`（NOTES-APP のように大文字で出る）のままで、同じプロジェクトの中で呼び名が画面ごとに変わっていた。
 *   スラッグは URL の部品で、名前が人の呼び名（サイドバーも名前が主・スラッグは下の小さな行）。
 */
export const projectEyebrow = (project: { name?: string | null; slug: string }): string => project.name?.trim() || project.slug;
